import { useMemo, useCallback, useEffect, useContext } from 'react';
import { type CountrySelector } from '@aidkitorg/types/lib/survey';
import { type Country } from '@aidkitorg/types/lib/address/states';
import { Select, type SelectOptionType } from "@aidkitorg/component-library";
import { useModularMarkdown } from '../Hooks/ModularMarkdown';
import { generateAccessibleLabel } from '../utils/generateAccessibleLabel';
import { LANG_DICTS, useLocalizedStrings } from '../Localization';
import InterfaceContext from '../Context';
import { type DistroQuestionProps } from './Props';

// English country names, keyed by ISO 3166-1 alpha-2 code. These are the base
// i18n strings, so they're complete and act as the fallback label for any
// language that hasn't translated a given country yet.
const ENGLISH_COUNTRY_NAMES = LANG_DICTS.en.countries as Record<string, string>;

export function CountrySelector({
  component,
  info,
  setInfo,
  questionProps,
}: DistroQuestionProps<CountrySelector>) {
  const L = useLocalizedStrings();
  const context = useContext(InterfaceContext);
  const header = useModularMarkdown({ content: component.header ?? '', info });
  const accessibleLabel = component.header
    ? generateAccessibleLabel(component.header[context.lang])
    : '';

  // Country names are localized by `country_code` in the i18n `countries`
  // strings, falling back to the English name for any language that hasn't
  // been translated yet.
  const countryNames = L.countries as Record<string, string> | undefined;

  // Labels are localized to the current language, so rebuild the options
  // whenever the language changes.
  const options = useMemo<SelectOptionType<Country>[]>(() => {
    return COUNTRY_CODES.map((country_code) => ({
      label: countryNames?.[country_code] ?? ENGLISH_COUNTRY_NAMES[country_code] ?? country_code,
      value: { country_code, en: ENGLISH_COUNTRY_NAMES[country_code] ?? country_code },
    }));
  }, [countryNames]);

  const selectedOption = useMemo(() => {
    return options.find(({ value }) => value.country_code === info[component.targetField + '_country_code']);
  }, [options, info[component.targetField + '_country_code']]);

  const handleSelectOption = useCallback((country: SelectOptionType<Country> | undefined) => {
    setInfo(component.targetField, country?.value.en ?? '');
    setInfo(component.targetField + '_country_code', country?.value.country_code ?? '');
  }, [component.targetField]);

  useEffect(() => {
    // A stored country code with no matching option is stale (e.g. a code that
    // is no longer in the list), so clear it to keep the select and info in sync.
    const storedCountryCode = info[component.targetField + '_country_code'];

    if (storedCountryCode && !selectedOption) {
      handleSelectOption(undefined);
    }
  }, [info[component.targetField + '_country_code'], selectedOption, handleSelectOption]);

  if ((questionProps['Additional Options'] || []).indexOf('Hidden') !== -1) {
    return <span></span>;
  }

  return (
    <Select
      options={options}
      value={selectedOption}
      onChange={handleSelectOption}
      disabled={questionProps.blockQuestionEdits}
      description={accessibleLabel}
      label={header}
      virtualized
      searchable
      portal
      clearable
    />
  );
};

// ISO 3166-1 country and territory codes a user can pick from. The list is
// static; localized display names are looked up by code in the i18n `countries`
// strings (see `ENGLISH_COUNTRY_NAMES` for the English fallback).
const COUNTRY_CODES = [
  'AF', 'AL', 'DZ', 'AS', 'AD', 'AO', 'AI', 'AQ', 'AG', 'AR', 'AM', 'AW',
  'AU', 'AT', 'AZ', 'BH', 'BD', 'BB', 'BY', 'BE', 'BZ', 'BJ', 'BM', 'BT',
  'BO', 'BA', 'BW', 'BV', 'BR', 'IO', 'VG', 'BN', 'BG', 'BF', 'BI', 'CI',
  'KH', 'CM', 'CA', 'CV', 'KY', 'CF', 'TD', 'CL', 'CN', 'CX', 'CC', 'CO',
  'KM', 'CG', 'CK', 'CR', 'HR', 'CU', 'CY', 'CZ', 'CD', 'DK', 'DJ', 'DM',
  'DO', 'TL', 'EC', 'EG', 'SV', 'GQ', 'ER', 'EE', 'ET', 'FO', 'FK', 'FJ',
  'FI', 'MK', 'FR', 'GF', 'PF', 'TF', 'GA', 'GE', 'DE', 'GH', 'GI', 'GR',
  'GL', 'GD', 'GP', 'GU', 'GT', 'GN', 'GW', 'GY', 'HT', 'HM', 'HN', 'HK',
  'HU', 'IS', 'IN', 'ID', 'IR', 'IQ', 'IE', 'IL', 'IT', 'JM', 'JP', 'JO',
  'KZ', 'KE', 'KI', 'KW', 'KG', 'LA', 'LV', 'LB', 'LS', 'LR', 'LY', 'LI',
  'LT', 'LU', 'MO', 'MG', 'MW', 'MY', 'MV', 'ML', 'MT', 'MH', 'MQ', 'MR',
  'MU', 'YT', 'MX', 'FM', 'MD', 'MC', 'MN', 'MS', 'MA', 'MZ', 'MM', 'NA',
  'NR', 'NP', 'NL', 'AN', 'NC', 'NZ', 'NI', 'NE', 'NG', 'NU', 'NF', 'KP',
  'MP', 'NO', 'OM', 'PK', 'PW', 'PA', 'PG', 'PY', 'PE', 'PH', 'PN', 'PL',
  'PT', 'PR', 'QA', 'RE', 'RO', 'RU', 'RW', 'ST', 'SH', 'KN', 'LC', 'PM',
  'VC', 'WS', 'SM', 'SA', 'SN', 'SC', 'SL', 'SG', 'SK', 'SI', 'SB', 'SO',
  'ZA', 'GS', 'KR', 'ES', 'LK', 'SD', 'SR', 'SJ', 'SZ', 'SE', 'CH', 'SY',
  'TW', 'TJ', 'TZ', 'TH', 'BS', 'GM', 'TG', 'TK', 'TO', 'TT', 'TN', 'TR',
  'TM', 'TC', 'TV', 'VI', 'UG', 'UA', 'AE', 'GB', 'US', 'UM', 'UY', 'UZ',
  'VU', 'VA', 'VE', 'VN', 'WF', 'EH', 'YE', 'YU', 'ZM', 'ZW',
];
